import { ApolloClient, createHttpLink, InMemoryCache, from } from "@apollo/client"
import { onError } from "@apollo/client/link/error"
import { generatePath } from "react-router-dom"
import { omit, get } from "lodash"

import { API_SERVER_BASEDATA } from "./constants/api"
import { store } from "./store"
import { history } from "./history"
import { checkTokenExpired } from "./utils/http"
import { Routes } from "./constants/routes"
import AppMode from "./enums/AppMode"
import { preparePrefixUrl } from "./utils/api"
import { getScreenId, getTerminalId } from "./sagas/selectors"

const linkOpts = {
  fetch: (uri, options) => {
    const state = store.getState()
    const { token: standardToken } = state.user.authentication
    const { token: tokenTerminal } = state.user.terminalAuthentication
    const { id: simulationId } = state.simulation
    const { baselineid: baselineId } = options.headers

    const { mode } = state.appConfig

    let token = null

    if (mode === AppMode.Terminal) {
      token = tokenTerminal
    } else {
      token = standardToken
    }

    const headers = omit(options.headers, ["authorization", "baselineid"])
    if (token) {
      headers.authorization = `Bearer ${token}`
      if (mode === AppMode.Terminal) {
        const terminalId = getTerminalId(state)
        if (terminalId) headers.terminal = `${terminalId}`
      } else if (mode === AppMode.Screen) {
        const screenId = getScreenId(state)
        if (screenId) headers.screen = `${screenId}`
      }
    }

    const opts = {
      ...options,
      credentials: token ? "same-origin" : "include",
      headers,
    }
    const uriFull = preparePrefixUrl(`${uri}/graphql`, baselineId || simulationId)
    return fetch(uriFull, opts)
  },
  uri: API_SERVER_BASEDATA,
}

const errorLink = onError(({ graphQLErrors, networkError }) => {
  let shouldLogout = false

  //
  // NETWORK ERRORS
  // (401, 403, 500, etc.)
  //
  const statusCode = get(networkError, "statusCode", 0)

  const networkMessage = get(networkError, "result.message", "") || get(networkError, "message", "")

  const isTokenExpired = checkTokenExpired(networkMessage)

  if (networkError && statusCode >= 400 && isTokenExpired) {
    shouldLogout = true
  }

  //
  // GRAPHQL ERRORS
  // (HTTP 200 + errors[])
  //
  const state = store.getState()
  const { mode } = state.appConfig
  if (graphQLErrors && graphQLErrors.length > 0) {
    graphQLErrors.forEach(error => {
      const { message } = error

      const isDeviceError = message === "NumberOfDevicesExceeded" || message === "DeviceNotLinked"

      if (mode !== AppMode.Standard && isDeviceError) {
        const shouldReload = mode === AppMode.Screen || (mode === AppMode.Terminal && message === "DeviceNotLinked")

        if (shouldReload) {
          window.location.reload()
        } else {
          shouldLogout = true
        }
      }
    })
  }

  if (shouldLogout) {
    const { terminal } = get(state, "user.terminal", {})
    if (terminal) {
      history.push(
        generatePath(Routes.AUTO_LOGOUT_TERMINAL, {
          terminal,
        })
      )
    } else {
      history.push(Routes.AUTO_LOGOUT)
    }
  }
})

const client = new ApolloClient({
  cache: new InMemoryCache({
    typePolicies: {
      ResourceAvailabilityPlanGraphType: {
        keyFields: ["id", "validFrom", "validTo"],
      },
    },
  }),
  connectToDevTools: true,
  defaultOptions: {
    watchQuery: {
      fetchPolicy: "cache-and-network",
      errorPolicy: "all",
    },
  },
  link: from([errorLink, createHttpLink(linkOpts)]),
})

export default client
